import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import type { FC } from "react"

import { useCheckedInBuiltPalletsColumns } from "./hooks/useCheckedInBuiltPalletsColumns"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { GoodsReceiptPallet, WarehouseArea } from "@/graphql/codegen/graphql"
import { isPalletAs400SyncPending } from "@/screens/WarehouseReceiving/utils"

interface CheckedInBuiltPalletsTableProps {
  pallets: GoodsReceiptPallet[]
  warehouse?: WarehouseArea | null
  rowSelection: RowSelectionState
  isAkrochemManaged?: boolean
  isSyncing?: boolean
  isLoading?: boolean
  canMaintain?: boolean
  openActionsMenuId: string | null
  onActionsMenuOpen: (palletId: string) => void
  onActionsMenuClose: () => void
  onDestinationOverride?: (pallet: GoodsReceiptPallet) => void
  onLpnEdit?: (pallet: GoodsReceiptPallet) => void
  onQrPrint?: (pallet: GoodsReceiptPallet) => void
  onDelete?: (pallet: GoodsReceiptPallet) => void
  onRowSelectionChange: OnChangeFn<RowSelectionState>
}

export const CheckedInBuiltPalletsTable: FC<CheckedInBuiltPalletsTableProps> = ({
  pallets,
  warehouse,
  rowSelection,
  isAkrochemManaged = true,
  isSyncing = false,
  isLoading = false,
  canMaintain = true,
  openActionsMenuId,
  onActionsMenuOpen,
  onActionsMenuClose,
  onDestinationOverride,
  onLpnEdit,
  onQrPrint,
  onDelete,
  onRowSelectionChange,
}) => {
  const columns = useCheckedInBuiltPalletsColumns({
    warehouse,
    isAkrochemManaged,
    isSyncing,
    canMaintain,
    openActionsMenuId,
    onActionsMenuOpen,
    onActionsMenuClose,
    onDestinationOverride,
    onLpnEdit,
    onQrPrint,
    onDelete,
  })

  return (
    <AerosBaseTable<GoodsReceiptPallet>
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data: pallets,
          columns,
          enableSorting: false,
          enableRowSelection: (row) =>
            !row.original.isStored &&
            !!row.original.destinationWarehouseShelvingId &&
            !isPalletAs400SyncPending(row.original),
          onRowSelectionChange,
          state: { rowSelection, isLoading },
          getCoreRowModel: getCoreRowModel(),
          getRowId: (row) => row.goodsReceiptPalletId,
        },
      }}
      slotProps={{
        bodyRow: {
          getClassName: (row) => (row.original.isStored ? "bg-green-50" : ""),
        },
      }}
      noDataMessage='No pallets found'
    />
  )
}
